import {
  FunctionComponent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import TextFieldAtom from "../../../atoms/textfield";
import {
  LabelPairedCalendarSmBoldIcon,
  LabelPairedChevronDownSmBoldIcon,
  LabelPairedChevronLeftSmBoldIcon,
  LabelPairedChevronRightSmBoldIcon,
} from "@deriv/quill-icons";

import { useClickOutside } from "../../../../src/hooks/useClickOutside";
import DropdownContainerAtom from "../../../../src/atoms/dropdown/container";
import "react-calendar/dist/Calendar.css";
import "./Datepicker.css";
import Calendar from "react-calendar";
import { format, parse } from "date-fns";
import { createRoot } from "react-dom/client";
import IconButton from "../../buttons/icon";

export type DatePickerType = {
  className?: string;
  label?: string;
  placeholder?: string;
  showCharacterCounter?: boolean;
  message?: string;

  /** Variant props */
  indicator?: "neutral" | "fail" | "success";
  state?: "default" | "disabled";
  type?: "outline" | "fill";
  size?: "lg" | "md" | "sm";
  textAlignment?: "left" | "center";
  variant?: "labelled" | "labelless";

  /** Custom props */
  isRequired?: boolean;
  optionalText?: string;
  maxLength?: number;
  value?: string | number;
  minDate?: string | Date;
  maxDate?: string | Date;
  onChange?: (value: string | number) => void;
  dateFormat?:
    | "dd/mm/yyyy"
    | "mm/dd/yyyy"
    | "yyyy/mm/dd"
    | "dd/mm/yy"
    | "mm/dd/yy"
    | "yyyy/mm/yy"
    | "dd-mm-yyyy"
    | "mm-dd-yyyy"
    | "yyyy-mm-dd"
    | "dd-mm-yy"
    | "mm-dd-yy";
  messages?: {
    text: string;
    type: "neutral" | "fail" | "success";
    icon?: ReactNode;
  }[];
};

export const Datepicker: FunctionComponent<DatePickerType> = ({
  dateFormat = "dd/mm/yyyy",
  size = "md",
  minDate,
  maxDate,
  ...props
}) => {
  const [value, setValue] = useState(props.value);
  const [isOpen, setIsOpen] = useState(false);
  const datepickerRef = useRef<HTMLDivElement>(null);
  const prevFormatRef = useRef(dateFormat);

  const datepickerContainerOffets = {
    sm: 15,
    md: 20,
    lg: 35,
  };

  const mapFormatString = (dateFormat: string) => {
    return dateFormat
      .replace(/yyyy/g, "yyyy")
      .replace(/yy/g, "yy")
      .replace(/mm/g, "MM")
      .replace(/dd/g, "dd");
  };

  const parseToDate = (date?: string | Date) => {
    if (!date) return undefined;
    if (date instanceof Date) return date;
    // Try to parse string to Date
    const parsed = new Date(date);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  };

  // Helper to parse value according to dateFormat
  const getValidDate = (
    value: string | number | undefined,
    dateFormat: string
  ) => {
    if (!value) return null;
    if (typeof value === "string") {
      const formatString = mapFormatString(dateFormat);
      const parsed = parse(value, formatString, new Date());
      return !isNaN(parsed.getTime()) ? parsed : null;
    }
    if (typeof value === "number") {
      const date = new Date(value);
      return !isNaN(date.getTime()) ? date : null;
    }
    return null;
  };

  const handleClick = (e) => {
    if (props.state === "disabled") return;

    if (!e.target.closest(".quill-ui-next-calendar")) {
      setIsOpen(!isOpen);
    }
  };

  const handleChange = (date: Date) => {
    const formatString = mapFormatString(dateFormat);
    const formattedDate = format(date, formatString);
    datepickerRef.current?.focus();
    setValue(formattedDate);
    props.onChange?.(formattedDate);
    setTimeout(() => {
      setIsOpen(false);
    }, 100);
  };

  // Update custom react-datepicker iconss
  useEffect(() => {
    if (isOpen) {
      const calendar = document.querySelector(".quill-ui-next-calendar");
      const setNavIcon = (selector: string, icon: React.ReactNode) => {
        const nav = calendar?.querySelector(selector);
        if (nav) {
          nav.innerHTML = ""; // Clear existing children
          const container = document.createElement("span");
          nav.appendChild(container);
          createRoot(container).render(icon);
        }
      };
      setNavIcon(
        ".react-calendar__navigation__prev-button",
        <IconButton
          style="tertiary"
          size="sm"
          icon={<LabelPairedChevronLeftSmBoldIcon />}
          color="blackWhite"
        />
      );
      setNavIcon(
        ".react-calendar__navigation__next-button",
        <IconButton
          size="sm"
          style="tertiary"
          icon={<LabelPairedChevronRightSmBoldIcon />}
          color="blackWhite"
        />
      );
    }
  }, [isOpen]);

  useEffect(() => {
    if (value && prevFormatRef.current !== dateFormat) {
      // Parse using the previous format
      const prevFormatString = mapFormatString(prevFormatRef.current);
      const date = parse(value as string, prevFormatString, new Date());
      if (!isNaN(date.getTime())) {
        // Format to the new format
        const newFormatString = mapFormatString(dateFormat);
        const newValue = format(date, newFormatString);
        setValue(newValue);
        props.onChange?.(newValue);
      }
      prevFormatRef.current = dateFormat;
    }
  }, [dateFormat]);

  useClickOutside(
    datepickerRef,
    () => {
      setIsOpen(false);
    },
    ["quill-ui-next-calendar"]
  );

  return (
    <TextFieldAtom
      ref={datepickerRef as unknown as React.Ref<HTMLInputElement>}
      {...props}
      size={size}
      allowTyping={false}
      placeholder={dateFormat}
      iconLeft={<LabelPairedCalendarSmBoldIcon />}
      iconRight={
        <LabelPairedChevronDownSmBoldIcon
          className={`quill-ui-next-rotatable ${
            isOpen && "quill-ui-next-rotate"
          }`}
        />
      }
      onChange={(e: string | number) => {
        setValue(e);
        props.onChange?.(e);
      }}
      onClick={handleClick}
      value={value}
      className={`${props.state !== "disabled" && "quill-ui-next-pointer"}`}
      customContent={
        <DropdownContainerAtom
          width="fit-content"
          textAlignment={props.textAlignment}
          parent={datepickerRef}
          isOpen={isOpen}
          offsetTop={
            props.variant === "labelless" ? datepickerContainerOffets[size] : 35
          }
          onActionSheetClose={() => setIsOpen(false)}
          customContent={
            <Calendar
              value={getValidDate(value, dateFormat)}
              onChange={(value, event) => {
                if (value instanceof Date) handleChange(value);
              }}
              locale="en-GB"
              showNeighboringMonth={false}
              className="quill-ui-next-calendar"
              minDate={parseToDate(minDate)}
              maxDate={parseToDate(maxDate)}
            />
          }
        />
      }
    />
  );
};

export default Datepicker;
